/*
 * SCREENS LOADED ON DEMAND (decision 990): the admin screens, which every
 * coach and parent downloaded and none could open -- the API answers 403 to a
 * non-admin. Each is its own file, fetched the first time its route renders.
 *
 * A DEPLOY DELETES THE OLD FILES. The Go binary serves only the build it
 * embeds, so a page left open across a deploy asks for a hash that is gone,
 * and the import fails. The page's own build is what is stale, so the remedy
 * is to reload it ONCE, which fetches the new shell and its new hashes. A
 * second failure soon after is not staleness -- the connection is down, or the
 * server is -- and reloading again would loop, so it says so instead, in the
 * words the app already uses for a load that failed.
 *
 * The rest of the app is unaffected: everything not named here still loads
 * with the page, so this costs nothing off the admin routes.
 */

import { Component, lazy, Suspense, type ComponentType, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

/** How long after a reload a second failure counts as the same one. */
export const RELOAD_WINDOW_MS = 30_000;

const RELOADED_AT = "tc.lazyScreenReloadedAt";

/**
 * Whether a failed load should reload the page: not if it reloaded for one
 * within the window, which is what stops a dead connection looping.
 */
export function shouldReload(reloadedAt: number | null, now: number): boolean {
  return reloadedAt === null || now - reloadedAt > RELOAD_WINDOW_MS;
}

// sessionStorage can be absent or throw (a private window, blocked storage).
// Without it the page cannot know whether it already reloaded, so it does not
// reload at all: a loop is worse than the honest sentence.
function readReloadedAt(): number | null | undefined {
  try {
    const v = window.sessionStorage.getItem(RELOADED_AT);
    return v === null ? null : Number(v);
  } catch {
    return undefined;
  }
}

function writeReloadedAt(now: number): boolean {
  try {
    window.sessionStorage.setItem(RELOADED_AT, String(now));
    return true;
  } catch {
    return false;
  }
}

function retrying<P extends object>(load: () => Promise<ComponentType<P>>) {
  return async (): Promise<{ default: ComponentType<P> }> => {
    try {
      return { default: await load() };
    } catch (err) {
      const now = Date.now();
      const reloadedAt = readReloadedAt();
      if (reloadedAt !== undefined && shouldReload(reloadedAt, now) && writeReloadedAt(now)) {
        window.location.reload();
        // Never settles: the fallback holds until the reload replaces the page.
        return new Promise(() => {});
      }
      throw err;
    }
  };
}

function LoadFailed() {
  const { t } = useTranslation();
  return (
    <div style={{ padding: "var(--tc-space-4)" }}>
      <p>{t("error.couldNotLoad")}</p>
    </div>
  );
}

class LoadBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override render() {
    return this.state.failed ? <LoadFailed /> : this.props.children;
  }
}

/**
 * A screen fetched the first time it renders. `load` names the export, since
 * the screens are named exports: `lazyScreen(() =>
 * import("../screens/Metrics").then((m) => m.Metrics))`.
 */
export function lazyScreen<P extends object>(load: () => Promise<ComponentType<P>>): ComponentType<P> {
  const Screen = lazy(retrying(load));
  return function LazyScreen(props: P) {
    // No fallback drawn: the file is small and the shell's chrome stays, so a
    // placeholder would only flash.
    return (
      <LoadBoundary>
        <Suspense fallback={null}>
          <Screen {...props} />
        </Suspense>
      </LoadBoundary>
    );
  };
}
